import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Button,
  Tag,
  Space,
  Input,
  Modal,
  message,
  Typography,
  Popconfirm,
  Tooltip,
  Select,
  Rate,
  Avatar,
  Spin,
  Row,
  Col,
  Statistic,
} from "antd";
import {
  SearchOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  DownloadOutlined,
  FilterOutlined,
  ReloadOutlined,
  UserOutlined,
  GlobalOutlined,
  DeleteOutlined,
  BankOutlined,
} from "@ant-design/icons";
import { testimonialService } from "../../../services";
import { companyService } from "../../../services/companyService";
import { authService } from "../../../services/authService";
import VideoPreviewModal from "../../../hooks/VideoPreviewModal";

const { Title, Text } = Typography;
const { Option } = Select;

const Testimonials = () => {
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  const [isVideoModalVisible, setIsVideoModalVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [testimonials, setTestimonials] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [pageCache, setPageCache] = useState({});
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    userTestimonials: 0,
    guestSubmissions: 0,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Get current user info
  const currentUser = authService.getCurrentUser();
  const isSuperAdmin = currentUser?.role === "super_admin";

  // Load companies for super_admin
  const loadCompanies = async () => {
    if (!isSuperAdmin) return;

    try {
      setCompaniesLoading(true);
      const response = await companyService.getCompanies(1, 1000);
      if (response.status) {
        setCompanies(response.data.companies || []);
      }
    } catch (error) {
      console.error("Load companies error:", error);
    } finally {
      setCompaniesLoading(false);
    }
  };

  // Load testimonials with caching
  const loadTestimonials = async (
    page = 1,
    pageSize = 10,
    forceRefresh = false,
  ) => {
    try {
      setLoading(true);

      const cacheKey = `${page}-${pageSize}-${searchText}-${statusFilter}-${typeFilter}`;

      if (!forceRefresh && pageCache[cacheKey]) {
        setTestimonials(pageCache[cacheKey].data);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: pageCache[cacheKey].total,
        });
        setLoading(false);
        return;
      }

      const response = await testimonialService.getAllTestimonials(
        page,
        pageSize,
        searchText,
        statusFilter === "all" ? "" : statusFilter,
        typeFilter === "all" ? "" : typeFilter,
      );

      if (response.status) {
        // Store in cache
        setPageCache((prev) => ({
          ...prev,
          [cacheKey]: {
            data: response.data.testimonials || [],
            total: response.data.pagination?.totalItems || 0,
          },
        }));

        setTestimonials(response.data.testimonials || []);
        setPagination({
          current: response.data.pagination?.currentPage || page,
          pageSize: pageSize,
          total: response.data.pagination?.totalItems || 0,
        });

        // Load stats separately
        await loadStats();
      } else {
        message.error(response.message || "Failed to load testimonials");
        setTestimonials([]);
        setPagination({
          current: 1,
          pageSize: pageSize,
          total: 0,
        });
      }
    } catch (error) {
      console.error("Load testimonials error:", error);
      message.error("Failed to load testimonials");
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  };

  // Load stats
  const loadStats = async () => {
    try {
      const response = await testimonialService.getTestimonialStats();
      if (response.status) {
        const statsData = response.data;

        // Handle company-specific stats if needed
        if (
          isSuperAdmin &&
          companyFilter !== "all" &&
          statsData?.companyWiseStats
        ) {
          const findCompanyStats = statsData.companyWiseStats.filter(
            (c) => c.companyId == companyFilter,
          );

          if (findCompanyStats && findCompanyStats?.length > 0) {
            setStats({
              total: findCompanyStats[0]?.total || 0,
              pending: findCompanyStats[0]?.pending || 0,
              approved: findCompanyStats[0]?.approved || 0,
              rejected: findCompanyStats[0]?.rejected || 0,
              userTestimonials: findCompanyStats[0]?.userTestimonials || 0,
              guestSubmissions: findCompanyStats[0]?.guestSubmissions || 0,
            });
          } else {
            setStats({
              total: 0,
              pending: 0,
              approved: 0,
              rejected: 0,
              userTestimonials: 0,
              guestSubmissions: 0,
            });
          }
        } else {
          setStats({
            total: statsData?.totalTestimonials || 0,
            pending: statsData?.pendingTestimonials || 0,
            approved: statsData?.approvedTestimonials || 0,
            rejected: statsData?.rejectedTestimonials || 0,
            userTestimonials: statsData?.userTestimonials || 0,
            guestSubmissions: statsData?.guestSubmissions || 0,
          });
        }
      }
    } catch (error) {
      console.error("Load stats error:", error);
    }
  };

  // Initial load on component mount
  useEffect(() => {
    loadTestimonials(1, pagination.pageSize);
    if (isSuperAdmin) {
      loadCompanies();
    }
  }, []);

  // Handle filter changes with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      // Clear all cache when filters change
      setPageCache({});
      loadTestimonials(1, pagination.pageSize);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText, statusFilter, typeFilter]);

  // Handle company filter change for stats
  useEffect(() => {
    if (isSuperAdmin) {
      loadStats();
    }
  }, [companyFilter]);

  // Reset company filter if user is not super admin
  useEffect(() => {
    if (!isSuperAdmin && companyFilter !== "all") {
      setCompanyFilter("all");
    }
  }, [isSuperAdmin]);

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i];
  };

  const handleTableChange = (newPagination) => {
    loadTestimonials(newPagination.current, newPagination.pageSize);
  };

  const handlePreview = (testimonial) => {
    setSelectedTestimonial(testimonial);
    setIsPreviewModalVisible(true);
  };

  const handleApprove = async (testimonialId) => {
    try {
      const response = await testimonialService.updateTestimonialStatus(
        testimonialId,
        "approved",
      );

      if (response.status) {
        message.success("Testimonial approved successfully!");
        if (isPreviewModalVisible) {
          setIsPreviewModalVisible(false);
        }
        // Clear cache for current filters since data has changed
        setPageCache({});
        await loadTestimonials(pagination.current, pagination.pageSize);
      } else {
        message.error(response.message || "Failed to approve testimonial");
      }
    } catch (error) {
      console.error("Approve testimonial error:", error);
      message.error("Failed to approve testimonial");
    }
  };

  const handleDelete = async (testimonialId) => {
    try {
      const response =
        await testimonialService.deleteTestimonial(testimonialId);

      if (response.status) {
        message.success("Testimonial deleted successfully!");
        // Clear cache since data has changed
        setPageCache({});
        // If this was the last item on the page, go to previous page
        if (testimonials.length === 1 && pagination.current > 1) {
          await loadTestimonials(pagination.current - 1, pagination.pageSize);
        } else {
          await loadTestimonials(pagination.current, pagination.pageSize);
        }
      } else {
        message.error(response.message || "Failed to delete testimonial");
      }
    } catch (error) {
      console.error("Delete testimonial error:", error);
      message.error("Failed to delete testimonial: " + error.message);
    }
  };

  const handleReject = async (testimonialId) => {
    try {
      const response = await testimonialService.updateTestimonialStatus(
        testimonialId,
        "rejected",
      );

      if (response.status) {
        message.success("Testimonial rejected!");
        if (isPreviewModalVisible) {
          setIsPreviewModalVisible(false);
        }
        // Clear cache for current filters since data has changed
        setPageCache({});
        await loadTestimonials(pagination.current, pagination.pageSize);
      } else {
        message.error(response.message || "Failed to reject testimonial");
      }
    } catch (error) {
      console.error("Reject testimonial error:", error);
      message.error("Failed to reject testimonial");
    }
  };

  const handleRefresh = () => {
    // Clear cache for current filter combination
    const currentCacheKey = `${pagination.current}-${pagination.pageSize}-${searchText}-${statusFilter}-${typeFilter}`;
    setPageCache((prev) => {
      const newCache = { ...prev };
      delete newCache[currentCacheKey];
      return newCache;
    });
    loadTestimonials(pagination.current, pagination.pageSize, true);
  };

  const getUserDisplayInfo = (testimonial) => {
    if (testimonial.isGuestSubmission) {
      return {
        name: testimonial.guestName || "Guest User",
        email: testimonial.guestEmail || "",
        avatar: testimonial.guestName
          ? testimonial.guestName.substring(0, 2).toUpperCase()
          : "G",
        isGuest: true,
        company: testimonial.company,
      };
    } else {
      if (testimonial.user) {
        return {
          name: testimonial.user.name || "Unknown User",
          email: testimonial.user.email || "",
          avatar: testimonial.user.name
            ? testimonial.user.name.substring(0, 2).toUpperCase()
            : "U",
          isGuest: false,
          company: testimonial.user.company,
        };
      }
      return {
        name: "Unknown User",
        email: "",
        avatar: "UU",
        isGuest: false,
        company: testimonial.company,
      };
    }
  };

  const getReviewerInfo = (testimonial) => {
    if (testimonial.reviewer) {
      return {
        name: testimonial.reviewer.name || "Admin",
        email: testimonial.reviewer.email || "",
      };
    }
    return {
      name: "Not reviewed",
      email: "",
    };
  };

  const handleDownloadVideo = async (videoUrl, filename) => {
    try {
      message.loading({ content: "Downloading video...", key: "download" });

      const response = await fetch(videoUrl, {
        method: "GET",
        headers: {
          "Content-Type": "video/webm",
        },
      });

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();

      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || videoUrl.split("/").pop() || "video.webm";

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      message.success({ content: "Download completed!", key: "download" });
    } catch (error) {
      console.error("Download error:", error);
      message.error({ content: "Failed to download video", key: "download" });
    }
  };

  const columns = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (user, record) => {
        const userInfo = getUserDisplayInfo(record);
        return (
          <Space direction="vertical" size="small">
            <Space>
              <Avatar
                size="small"
                style={{
                  backgroundColor: userInfo.isGuest ? "#52c41a" : "#1890ff",
                }}
                icon={userInfo.isGuest ? <GlobalOutlined /> : <UserOutlined />}
              >
                {userInfo.avatar}
              </Avatar>
              <div>
                <div className="font-medium">
                  {userInfo.name}
                  {userInfo.isGuest && (
                    <Tag color="green" size="small" style={{ marginLeft: 8 }}>
                      Guest
                    </Tag>
                  )}
                </div>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {userInfo.email}
                </Text>
              </div>
            </Space>
            {userInfo.company && (
              <Space size="small">
                <BankOutlined style={{ color: "#1890ff", fontSize: "12px" }} />
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {userInfo.company.name}
                </Text>
              </Space>
            )}
          </Space>
        );
      },
    },
    {
      title: "File Size",
      dataIndex: "fileSize",
      key: "fileSize",
      render: (fileSize) => <Text>{formatFileSize(fileSize)}</Text>,
    },
    {
      title: "Rating",
      dataIndex: "rating",
      key: "rating",
      render: (rating) => (
        <Rate disabled defaultValue={rating || 0} size="small" />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const statusConfig = {
          pending: { color: "orange", text: "Pending Review" },
          approved: { color: "green", text: "Approved" },
          rejected: { color: "red", text: "Rejected" },
        };
        const config = statusConfig[status] || {
          color: "default",
          text: status,
        };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: "Submitted",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "N/A"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        return (
          <Space size="small">
            <Tooltip title="Preview">
              <Button
                icon={<EyeOutlined />}
                size="small"
                onClick={() => handlePreview(record)}
              />
            </Tooltip>
            {record.videoUrl && (
              <>
                <Tooltip title="Play Video">
                  <Button
                    icon={<PlayCircleOutlined />}
                    size="small"
                    type="primary"
                    ghost
                    onClick={() => {
                      setSelectedTestimonial(record);
                      setIsVideoModalVisible(true);
                    }}
                  />
                </Tooltip>

                <Tooltip title="Download Video">
                  <Button
                    icon={<DownloadOutlined />}
                    size="small"
                    type="default"
                    onClick={(e) => {
                      e.stopPropagation();
                      const filename = `testimonial-${record.id}-${Date.now()}.webm`;
                      handleDownloadVideo(record.videoUrl, filename);
                    }}
                  />
                </Tooltip>
              </>
            )}
            <Popconfirm
              title="Delete this testimonial?"
              description="Are you sure you want to delete this testimonial? This action cannot be undone and will also delete the video file."
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
              okType="danger"
            >
              <Tooltip title="Delete">
                <Button icon={<DeleteOutlined />} size="small" danger />
              </Tooltip>
            </Popconfirm>
            {record.status === "pending" && (
              <>
                <Tooltip title="Approve">
                  <Button
                    icon={<CheckCircleOutlined />}
                    size="small"
                    type="primary"
                    ghost
                    onClick={() => handleApprove(record.id)}
                  />
                </Tooltip>
                <Tooltip title="Reject">
                  <Button
                    icon={<CloseCircleOutlined />}
                    size="small"
                    danger
                    ghost
                    onClick={() => handleReject(record.id)}
                  />
                </Tooltip>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <Title level={2}>Testimonials</Title>
          <Text type="secondary">Review and manage all testimonials</Text>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
            size="large"
          >
            Refresh
          </Button>
        </Space>
      </div>

      {/* Statistics */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={12} sm={6}>
          <Card className="text-center">
            <Statistic
              title="Total"
              value={stats.total}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="text-center">
            <Statistic
              title="Pending"
              value={stats.pending}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="text-center">
            <Statistic
              title="Approved"
              value={stats.approved}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="text-center">
            <Statistic
              title="Rejected"
              value={stats.rejected}
              valueStyle={{ color: "#ff4d4f" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="text-center">
            <Statistic
              title="User Testimonials"
              value={stats.userTestimonials}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="text-center">
            <Statistic
              title="Guest Submissions"
              value={stats.guestSubmissions}
              valueStyle={{ color: "#13c2c2" }}
            />
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm">
        <div className="flex justify-between mb-4 flex-wrap gap-2">
          <Input
            placeholder="Search testimonials..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            size="large"
            allowClear
          />
          <Space>
            <Select
              value={typeFilter}
              onChange={setTypeFilter}
              style={{ width: 150 }}
              size="large"
              allowClear
            >
              <Option value="all">All Types</Option>
              <Option value="user">User Testimonials</Option>
              <Option value="guest">Guest Submissions</Option>
            </Select>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 150 }}
              size="large"
              allowClear
            >
              <Option value="all">All Status</Option>
              <Option value="pending">Pending</Option>
              <Option value="approved">Approved</Option>
              <Option value="rejected">Rejected</Option>
            </Select>
          </Space>
        </div>

        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={testimonials}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Total ${total} testimonials`,
            }}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
            rowKey="id"
          />
        </Spin>
      </Card>

      {/* Preview Modal */}
      <Modal
        title="Testimonial Preview"
        open={isPreviewModalVisible}
        onCancel={() => setIsPreviewModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedTestimonial && (
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Avatar
                size="large"
                style={{
                  backgroundColor: selectedTestimonial.isGuestSubmission
                    ? "#52c41a"
                    : "#1890ff",
                }}
                icon={
                  selectedTestimonial.isGuestSubmission ? (
                    <GlobalOutlined />
                  ) : (
                    <UserOutlined />
                  )
                }
              >
                {getUserDisplayInfo(selectedTestimonial).avatar}
              </Avatar>
              <div>
                <Title level={4} className="mb-0">
                  {getUserDisplayInfo(selectedTestimonial).name}
                  {selectedTestimonial.isGuestSubmission && (
                    <Tag color="green" style={{ marginLeft: 8 }}>
                      Guest Submission
                    </Tag>
                  )}
                </Title>
                <Text type="secondary">
                  {getUserDisplayInfo(selectedTestimonial).email}
                </Text>
                {getUserDisplayInfo(selectedTestimonial).company && (
                  <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                    <BankOutlined style={{ marginRight: 4 }} />
                    {getUserDisplayInfo(selectedTestimonial).company.name}
                  </div>
                )}
              </div>
            </div>

            {selectedTestimonial.description && (
              <div>
                <Title level={5}>Description</Title>
                <Text>{selectedTestimonial.description}</Text>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Title level={5}>File Size</Title>
                <Tag>{formatFileSize(selectedTestimonial.fileSize)}</Tag>
              </div>
              <div>
                <Title level={5}>Rating</Title>
                <Rate disabled defaultValue={selectedTestimonial.rating || 0} />
              </div>
              <div>
                <Title level={5}>Status</Title>
                <Tag
                  color={
                    selectedTestimonial.status === "approved"
                      ? "green"
                      : selectedTestimonial.status === "rejected"
                        ? "red"
                        : "orange"
                  }
                >
                  {selectedTestimonial.status}
                </Tag>
              </div>
            </div>

            {selectedTestimonial.reviewer && (
              <div>
                <Title level={5}>Reviewed By</Title>
                <Text>{getReviewerInfo(selectedTestimonial).name}</Text>
              </div>
            )}

            {selectedTestimonial.reviewNotes && (
              <div>
                <Title level={5}>Review Notes</Title>
                <Text>{selectedTestimonial.reviewNotes}</Text>
              </div>
            )}

            {selectedTestimonial.videoUrl ? (
              <div
                className="bg-gray-100 p-6 rounded-lg text-center cursor-pointer hover:bg-gray-200 transition-all border border-gray-300"
                onClick={() => {
                  setIsPreviewModalVisible(false);
                  setIsVideoModalVisible(true);
                }}
              >
                <div className="relative inline-block mb-4">
                  <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                    <PlayCircleOutlined className="text-3xl text-white ml-1" />
                  </div>
                </div>
                <div className="font-medium text-lg mb-1 text-gray-800">
                  Video Preview Available
                </div>
                <Text type="secondary" className="block mb-3">
                  {selectedTestimonial.videoUrl.split("/").pop()}
                </Text>
                <div className="flex justify-center space-x-3 mt-4">
                  <Button
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsPreviewModalVisible(false);
                      setIsVideoModalVisible(true);
                    }}
                    size="large"
                    className="shadow-md"
                  >
                    Play Video
                  </Button>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      const filename = `testimonial-${selectedTestimonial.id}-${Date.now()}.webm`;
                      handleDownloadVideo(
                        selectedTestimonial.videoUrl,
                        filename,
                      );
                    }}
                    size="large"
                  >
                    Download Video
                  </Button>
                  <a
                    href={selectedTestimonial.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button icon={<DownloadOutlined />} size="large">
                      Open in Tab
                    </Button>
                  </a>
                </div>
                <div className="mt-3 text-xs text-gray-500">
                  Click anywhere in this box to open video player
                </div>
              </div>
            ) : (
              <div className="bg-gray-100 p-6 rounded-lg text-center border border-gray-200">
                <PlayCircleOutlined className="text-4xl text-gray-300 mb-3" />
                <div className="font-medium text-gray-600">
                  No Video Available
                </div>
                <Text type="secondary">
                  This testimonial doesn't have a video attachment
                </Text>
              </div>
            )}

            {selectedTestimonial.status === "pending" && (
              <div className="flex space-x-2 justify-end pt-4 border-t">
                <Button
                  icon={<CloseCircleOutlined />}
                  onClick={() => handleReject(selectedTestimonial.id)}
                  danger
                >
                  Reject
                </Button>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleApprove(selectedTestimonial.id)}
                >
                  Approve
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      <VideoPreviewModal
        videoUrl={selectedTestimonial?.videoUrl}
        isVisible={isVideoModalVisible}
        onClose={() => setIsVideoModalVisible(false)}
        title={`Video Preview: ${
          selectedTestimonial?.title || "Testimonial Video"
        }`}
      />
    </div>
  );
};

export default Testimonials;
